import { Outlet, useParams } from 'react-router';
import { Server } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  StatusBadge,
  MoneyCell,
  DateCell,
  NotificationCard,
  Card,
  CpanelButton,
  CpanelLogo,
  useToast,
  type StatusTone,
} from '@afrilink/ui';
import { useService, serviceService, type ServiceStatus } from '@/domains/hosting';
import { relativeExpiry } from '@/shared/utils/format';

const CPANEL_ERROR_COPY: Record<string, string> = {
  email_not_verified: 'Verify your email address to access cPanel.',
  forbidden_not_owner: "You don't have access to this hosting account.",
  rate_limited: 'Too many cPanel sessions in the past hour — please wait a few minutes.',
  whm_session_failed: 'cPanel server is unreachable right now. Please try again.',
  whm_status_check_failed: 'Could not check service status with cPanel. Try again shortly.',
  cpanel_not_found: 'Hosting account not found.',
  cpanel_not_found_on_whm: 'Hosting account not found on the cPanel server.',
};

const STATUS_LABEL: Record<ServiceStatus, string> = {
  active: 'Active',
  pending: 'Pending',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
  terminated: 'Terminated',
  fraud: 'Under review',
};

const STATUS_TONE: Record<ServiceStatus, StatusTone> = {
  active: 'active',
  pending: 'pending',
  suspended: 'failed',
  cancelled: 'draft',
  terminated: 'draft',
  fraud: 'overdue',
};

export default function ServiceDetailLayout() {
  const { id } = useParams();
  const { data, isLoading, error } = useService(id);
  const { toast } = useToast();

  const baseBreadcrumbs = [
    { label: 'Home', href: '/app' },
    { label: 'Services', href: '/app/services' },
  ];

  if (isLoading) {
    return (
      <UniversalPage
        title="Service"
        breadcrumbs={[...baseBreadcrumbs, { label: id ?? 'Detail' }]}
      >
        <UniversalLoadingState variant="spinner" caption="Loading service…" />
      </UniversalPage>
    );
  }

  if (error) {
    return (
      <UniversalPage
        title="Could not load service"
        breadcrumbs={[...baseBreadcrumbs, { label: id ?? 'Detail' }]}
      >
        <NotificationCard tone="danger" title="Could not load service" body={error.message} />
      </UniversalPage>
    );
  }

  if (!data) {
    return (
      <UniversalPage
        title="Service not found"
        breadcrumbs={[...baseBreadcrumbs, { label: id ?? 'Detail' }]}
      >
        <Card interactive={false} className="p-12 text-center text-sm text-muted-foreground">
          No service with id <code className="rounded-sm bg-muted px-1">{id}</code>.
        </Card>
      </UniversalPage>
    );
  }

  return (
    <UniversalPage
      title={
        <span className="inline-flex items-center gap-2">
          <Server className="h-5 w-5 text-muted-foreground" />
          {data.primaryDomain}
        </span>
      }
      description={data.plan.name}
      breadcrumbs={[...baseBreadcrumbs, { label: data.primaryDomain }]}
      meta={
        <>
          <StatusBadge tone={STATUS_TONE[data.status]}>
            {STATUS_LABEL[data.status]}
          </StatusBadge>
          <span className="text-muted-foreground">·</span>
          <span className="inline-flex items-center gap-1">
            Next due <DateCell value={data.nextDueDate} format="long" /> ({relativeExpiry(data.nextDueDate)})
          </span>
          <span className="text-muted-foreground">·</span>
          <MoneyCell value={data.recurringAmountZAR} />
        </>
      }
      actions={
        <CpanelButton
          service={{
            cpanel_username: data.cpanelUsername,
            status: data.status,
          }}
          onIssueSession={(cpanel_username) => serviceService.createCpanelSession({ cpanel_username })}
          onError={(err) => {
            toast({
              tone: 'warning',
              title: 'Could not open cPanel',
              body: CPANEL_ERROR_COPY[err.code] ?? err.message,
            });
          }}
          variant="primary"
          size="sm"
        />
      }
      tabs={[
        { label: 'Overview', to: '.',      end: true },
        { label: 'Limits',   to: 'limits' },
        { label: 'Addons',   to: 'addons' },
        { label: 'Billing',  to: 'billing' },
        { label: 'Manage',   to: 'manage' },
      ]}
      tabsRenderOutlet={false}
    >
      {data.status === 'active' && (
        <p className="-mt-4 text-sm text-muted-foreground">
          Open cPanel to build your site, install WordPress, manage email, and 400+ more tools.
        </p>
      )}
      <Outlet context={data} />
      <p className="mt-10 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
        <CpanelLogo size={16} aria-hidden /> Powered by cPanel
      </p>
    </UniversalPage>
  );
}
